import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Book,
  Lock,
  FileText,
  Database,
  Globe,
  Mail,
  ShieldCheck,
  Clock,
  CreditCard,
  Users,
} from "lucide-react";

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const Section = ({ id, title, icon: Icon, children }) => (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-6 hover:bg-orange-50 transition-colors text-left"
      >
        <div className="flex items-center gap-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Icon className="w-6 h-6 text-orange-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        </div>
        {expandedSections[id] ? (
          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {expandedSections[id] && <div className="px-6 pb-6">{children}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-sm p-8 border-b border-gray-200">
          <div className="text-left">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Lock className="w-8 h-8 text-orange-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                  TableCheck Inc. — Privacy Policy
                </h1>
                <p className="text-gray-600">
                  Last Updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-b-xl shadow-sm divide-y divide-gray-200">
          <Section id="introduction" title="1. Introduction" icon={Book}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>
                Protecting your personal data and privacy is of utmost importance to TableCheck Inc. ("TableCheck," "we," "us," or "our").
              </p>
              <p>
                This privacy policy explains how we collect, use, and process personal data when you use our website, mobile app, or services. By accessing or using Hiree, you acknowledge and consent to the practices outlined in this policy.
              </p>
            </div>
          </Section>

          <Section id="data-collection" title="2. Information We Collect" icon={Database}>
          <div className="space-y-6 prose max-w-none text-gray-600">
            <h3 className="text-lg font-semibold text-gray-800">2.1 Information You Provide to Us</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Full name, email address, phone number, and profile picture.</li>
              <li>Employment history, skills, education details, and references.</li>
              <li>Payment details for subscriptions or premium features.</li>
              <li>Messages and communication history with employers or job seekers.</li>
              <li>Feedback, ratings, and reviews on job listings and employers.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6">2.2 Information We Collect Automatically</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Device information (e.g., device model, OS, and browser type).</li>
              <li>IP address and geolocation data (if permission is granted).</li>
              <li>Website interaction data (e.g., pages visited, time spent, and click patterns).</li>
            </ul>
          </div>
        </Section>

          <Section id="usage" title="3. How We Use Your Information" icon={FileText}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>We use your data for various purposes, including:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Providing job-matching services</li>
                <li>Processing transactions and payments</li>
                <li>Preventing fraudulent activities</li>
                <li>Improving our platform experience</li>
              </ul>
            </div>
          </Section>

          <Section id="data-retention" title="4. Data Retention" icon={Clock}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>We retain your data based on the following principles:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Personal data is stored as long as necessary to provide services.</li>
                <li>Account information is retained while your account remains active.</li>
                <li>Financial records are retained for tax and legal compliance.</li>
                <li>Data may be deleted upon request, except where legally required.</li>
              </ul>
            </div>
          </Section>

          <Section id="user-rights" title="5. Your Rights" icon={Users}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>You have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>The right to access the personal data we hold about you.</li>
                <li>The right to correct any inaccurate or incomplete information.</li>
                <li>The right to request the deletion of your account and data.</li>
                <li>The right to restrict or object to the processing of your data.</li>
                <li>The right to data portability to another service provider.</li>
              </ul>
            </div>
          </Section>

          <Section id="cookies" title="6. Cookies & Tracking" icon={Globe}>
            <div className="prose max-w-none text-gray-600 space-y-4">
              <p>We use cookies to enhance user experience, track analytics, and improve services.</p>
            </div>
          </Section>

          <Section id="security" title="7. Security Measures" icon={ShieldCheck}>
          <div className="prose max-w-none text-gray-600 space-y-4">
            <p>We take the following security measures to protect your data:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Using **SSL encryption** to secure data transmission.</li>
              <li>Implementing **firewalls and intrusion detection systems**.</li>
              <li>Restricting data access to authorized personnel only.</li>
              <li>Regularly updating security policies to prevent cyber threats.</li>
              <li>Monitoring platform activity for suspicious or fraudulent behavior.</li>
            </ul>
          </div>
        </Section>

        <Section id="payments" title="8. Payments & Transactions" icon={CreditCard}>
          <div className="prose max-w-none text-gray-600 space-y-4">
            <p>Payment transactions are processed securely through third-party providers:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>All payments are encrypted and handled via **Stripe**.</li>
              <li>We do not store credit card details directly on our servers.</li>
              <li>Fraud prevention measures are in place to ensure payment security.</li>
              <li>Invoices and payment records are stored for legal compliance.</li>
            </ul>
          </div>
        </Section>

        <Section id="contact" title="9. Contact Us" icon={Mail}>
        <div className="prose max-w-none text-gray-600 space-y-4">
          <p>If you have any privacy-related concerns, you can contact us:</p>
          <div className="bg-orange-50 rounded-lg p-6">
            <p>📧 Email: <a href="mailto:privacy@tablecheck.com" className="text-orange-600 hover:text-orange-700">privacy@tablecheck.com</a></p>
            <p>📍 Address: 16192 Coastal Highway, Lewes, Delaware, 19958</p>
          </div>
          <p>For GDPR-related inquiries, please contact our Data Protection Officer (DPO) at the email above.</p>
        </div>
      </Section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;